"use strict";
/**
 * Strategic Overview Calculation Functions
 *
 * This module contains pure calculation functions for Strategic Overview metrics.
 * These functions are shared between frontend and backend to ensure consistency.
 *
 * All functions are pure (no side effects, no external dependencies) and can be
 * used in any JavaScript/TypeScript environment.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.STRATEGIC_OVERVIEW_DEFAULTS = void 0;
exports.calculateWeightedABT = calculateWeightedABT;
exports.calculateUnitMetrics = calculateUnitMetrics;
exports.calculateAggregatedMetrics = calculateAggregatedMetrics;
// ============================================================================
// Default Values
// ============================================================================
/**
 * Default values used in calculations when actual values are not provided
 */
exports.STRATEGIC_OVERVIEW_DEFAULTS = {
    /** Default ASR target percentage */
    ASR_TARGET: 90,
    /** Default productivity factor percentage */
    PRODUCTIVITY_FACTOR: 65,
    /** Default working days per month */
    WORKING_DAYS: 20,
    /** Default ABT (Average Handling Time) in seconds, used as fallback for FTE calculations */
    ABT_FALLBACK: 180,
    /** Working hours per day */
    HOURS_PER_DAY: 8,
    /** Seconds per hour */
    SECONDS_PER_HOUR: 3600,
};
// ============================================================================
// Calculation Functions
// ============================================================================
/**
 * Calculate weighted average ABT (Average Handling Time) from locations.
 * ABT is weighted by handledContracts - locations with more contracts have more weight.
 *
 * Formula: Σ(handledContracts × averageSeconds) / Σ(handledContracts)
 *
 * @param locations - Array of locations with handledContracts and averageSeconds
 * @returns Object with avgABT (weighted average in seconds) and totalHandled (sum of handled contracts)
 */
function calculateWeightedABT(locations) {
    let totalWeightedSeconds = 0;
    let totalHandled = 0;
    for (const loc of locations) {
        const handled = loc.handledContracts ?? 0;
        const seconds = loc.averageSeconds ?? 0;
        if (handled > 0) {
            totalWeightedSeconds += handled * seconds;
            totalHandled += handled;
        }
    }
    const avgABT = totalHandled > 0 ? totalWeightedSeconds / totalHandled : null;
    return { avgABT, totalHandled };
}
/**
 * Calculate metrics for a single unit.
 * Used when calculating metrics for individual working units.
 *
 * @param unit - The unit data to calculate metrics for
 * @returns Calculated metrics for the unit
 */
function calculateUnitMetrics(unit) {
    const forecastedValue = unit.forecastedValue ?? 0;
    const asrTarget = Number(unit.asrTarget) || exports.STRATEGIC_OVERVIEW_DEFAULTS.ASR_TARGET;
    const productivityFactor = Number(unit.productivityFactor) || exports.STRATEGIC_OVERVIEW_DEFAULTS.PRODUCTIVITY_FACTOR;
    const workingDays = unit.workingDays ?? 0;
    const locations = unit.locations || [];
    const adjustments = unit.adjustments || [];
    // Plan Handled = Plan Offered × (ASR Target / 100)
    const planHandled = forecastedValue > 0 ? forecastedValue * (asrTarget / 100) : null;
    // Total Potentials = Σ(location handledContracts)
    const totalPotentials = locations.reduce((sum, loc) => sum + (loc.handledContracts ?? 0), 0);
    // Plan ABT Gesamt = weighted average ABT
    const { avgABT: planABTGesamt } = calculateWeightedABT(locations);
    // ASR Prognose = Total Potentials / Plan Offered × 100
    const asrPrognose = forecastedValue > 0 ? (totalPotentials / forecastedValue) * 100 : null;
    // Call Delta = Total Potentials - Plan Handled
    const callDelta = planHandled !== null ? Math.round(totalPotentials - planHandled) : null;
    // Produktivitätsstunden = 8h × workingDays × (productivityFactor / 100)
    const productiveHours = workingDays > 0
        ? exports.STRATEGIC_OVERVIEW_DEFAULTS.HOURS_PER_DAY * workingDays * (productivityFactor / 100)
        : null;
    // FTE Delta = (Call Delta × Plan ABT Gesamt / 3600) / Produktivitätsstunden
    const fteDelta = callDelta !== null && planABTGesamt !== null && productiveHours !== null && productiveHours > 0
        ? (callDelta * planABTGesamt) / exports.STRATEGIC_OVERVIEW_DEFAULTS.SECONDS_PER_HOUR / productiveHours
        : null;
    // Adjustment sum (chancen_risiken + unverplant)
    const adjustmentSum = adjustments.reduce((sum, adj) => sum + adj.value, 0);
    // ASR with adjustments = ((Total Potentials + adjustmentSum) / Plan Offered) × 100
    const asrWithAdjustments = forecastedValue > 0 ? ((totalPotentials + adjustmentSum) / forecastedValue) * 100 : null;
    // FTE Delta with adjustments
    const callDeltaWithAdj = callDelta !== null ? callDelta + adjustmentSum : null;
    const fteDeltaWithAdjustments = callDeltaWithAdj !== null && planABTGesamt !== null && productiveHours !== null && productiveHours > 0
        ? (callDeltaWithAdj * planABTGesamt) / exports.STRATEGIC_OVERVIEW_DEFAULTS.SECONDS_PER_HOUR / productiveHours
        : null;
    return {
        planHandled,
        totalPotentials,
        planABTGesamt,
        asrPrognose,
        callDelta,
        productiveHours,
        fteDelta,
        adjustmentSum,
        asrWithAdjustments,
        fteDeltaWithAdjustments,
    };
}
/**
 * Calculate aggregated metrics for multiple units (e.g., all units in a channel/month).
 * Used when calculating channel-level summaries in the grid and for snapshot computed metrics.
 *
 * The function:
 * - Sums: forecastedValue, potentials, adjustments
 * - Averages: asrTarget, workingDays, productivityFactor (only from units that have them)
 * - Weighted Average: ABT (weighted by handledContracts)
 *
 * @param units - Array of units to aggregate
 * @returns Aggregated calculated metrics
 */
function calculateAggregatedMetrics(units) {
    // Aggregate raw values across all units
    let totalPlanOffered = 0;
    let totalPotentials = 0;
    let totalWeightedSeconds = 0;
    let totalHandledForABT = 0;
    let totalWorkingDays = 0;
    let totalProductivityFactor = 0;
    let unitsWithWorkingDays = 0;
    let unitsWithProductivity = 0;
    let adjustmentSum = 0;
    let hasAsrTarget = false;
    let totalAsrTarget = 0;
    let unitsWithAsrTarget = 0;
    for (const unit of units) {
        totalPlanOffered += unit.forecastedValue ?? 0;
        // ASR Target averaging
        if (unit.asrTarget != null) {
            hasAsrTarget = true;
            totalAsrTarget += Number(unit.asrTarget);
            unitsWithAsrTarget++;
        }
        // Working days and productivity factor for FTE calculation
        if (unit.workingDays != null && unit.workingDays > 0) {
            totalWorkingDays += unit.workingDays;
            unitsWithWorkingDays++;
        }
        if (unit.productivityFactor != null) {
            totalProductivityFactor += Number(unit.productivityFactor);
            unitsWithProductivity++;
        }
        // Locations: potentials and weighted ABT
        const locations = unit.locations || [];
        for (const loc of locations) {
            const handled = loc.handledContracts ?? 0;
            const seconds = loc.averageSeconds ?? 0;
            totalPotentials += handled;
            totalHandledForABT += handled;
            totalWeightedSeconds += handled * seconds;
        }
        // Adjustments
        const adjustments = unit.adjustments || [];
        for (const adj of adjustments) {
            adjustmentSum += adj.value;
        }
    }
    // Calculate derived metrics using averages
    const avgAsrTarget = hasAsrTarget
        ? totalAsrTarget / unitsWithAsrTarget
        : exports.STRATEGIC_OVERVIEW_DEFAULTS.ASR_TARGET;
    const avgWorkingDays = unitsWithWorkingDays > 0
        ? totalWorkingDays / unitsWithWorkingDays
        : exports.STRATEGIC_OVERVIEW_DEFAULTS.WORKING_DAYS;
    const avgProductivityFactor = unitsWithProductivity > 0
        ? totalProductivityFactor / unitsWithProductivity
        : exports.STRATEGIC_OVERVIEW_DEFAULTS.PRODUCTIVITY_FACTOR;
    // Plan Handled = Plan Offered × (ASR Target / 100)
    const planHandled = totalPlanOffered > 0 ? totalPlanOffered * (avgAsrTarget / 100) : null;
    // Plan ABT Gesamt = weighted average ABT
    const planABTGesamt = totalHandledForABT > 0 ? totalWeightedSeconds / totalHandledForABT : null;
    // ASR Prognose = Total Potentials / Plan Offered × 100
    const asrPrognose = totalPlanOffered > 0 ? (totalPotentials / totalPlanOffered) * 100 : null;
    // Call Delta = Total Potentials - Plan Handled
    const callDelta = planHandled !== null ? Math.round(totalPotentials - planHandled) : null;
    // Produktivitätsstunden = 8h × workingDays × (productivityFactor / 100)
    const productiveHours = avgWorkingDays > 0
        ? exports.STRATEGIC_OVERVIEW_DEFAULTS.HOURS_PER_DAY * avgWorkingDays * (avgProductivityFactor / 100)
        : null;
    // FTE Delta = (Call Delta × Plan ABT Gesamt / 3600) / Produktivitätsstunden
    // Uses fallback ABT if not available
    const avgABTForFTE = planABTGesamt ?? exports.STRATEGIC_OVERVIEW_DEFAULTS.ABT_FALLBACK;
    const fteDelta = callDelta !== null && productiveHours !== null && productiveHours > 0
        ? (callDelta * avgABTForFTE) / exports.STRATEGIC_OVERVIEW_DEFAULTS.SECONDS_PER_HOUR / productiveHours
        : null;
    // ASR with adjustments = ((Total Potentials + adjustmentSum) / Plan Offered) × 100
    const asrWithAdjustments = totalPlanOffered > 0 ? ((totalPotentials + adjustmentSum) / totalPlanOffered) * 100 : null;
    // FTE Delta with adjustments
    const callDeltaWithAdj = callDelta !== null ? callDelta + adjustmentSum : null;
    const fteDeltaWithAdjustments = callDeltaWithAdj !== null && productiveHours !== null && productiveHours > 0
        ? (callDeltaWithAdj * avgABTForFTE) / exports.STRATEGIC_OVERVIEW_DEFAULTS.SECONDS_PER_HOUR / productiveHours
        : null;
    return {
        planHandled,
        totalPotentials,
        planABTGesamt,
        asrPrognose,
        callDelta,
        productiveHours,
        fteDelta,
        adjustmentSum,
        asrWithAdjustments,
        fteDeltaWithAdjustments,
    };
}
